import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { Gauge, RotateCcw, Download, ChevronDown, TrendingUp, Loader2 } from 'lucide-react';
import api from '../api/api';
import CSICoachingReport from '../components/CSICoachingReport';
import CSICelebration from '../components/CSICelebration';
import CSIResultSkeleton from '../components/layout/CSIResultSkeleton';
import { tone, displayRating, overallHeadline, deriveSummary, orderResults } from '../utils/csiReport';

export default function CSIResult() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const firstName = user?.first_name || user?.FirstName || '';

    // A just-completed assessment navigates here with the fresh result + a
    // celebrate flag, so the score reveal plays without a refetch flash.
    const seeded = location.state?.result;
    const [result, setResult] = useState(seeded || null);
    const [loading, setLoading] = useState(!seeded);
    const [celebrate, setCelebrate] = useState(!!location.state?.celebrate);
    const [usage, setUsage] = useState(location.state?.usage || null);
    const [history, setHistory] = useState([]);
    const [switchingId, setSwitchingId] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // /my-result gives the latest report (skipped if we were seeded a
                // fresh one) plus this month's usage for the counter / upgrade CTA.
                const res = await api.get('/csi/my-result');
                if (!alive) return;
                if (!seeded) setResult(res.data?.result || null);
                setUsage(res.data?.usage || null);
            } catch {
                if (alive && !seeded) setResult(null);
            } finally {
                if (alive) setLoading(false);
            }
            try {
                const h = await api.get('/csi/my-history');
                if (alive) setHistory(h.data?.history || []);
            } catch {
                if (alive) setHistory([]);
            }
        })();
        return () => { alive = false; };
    }, [seeded]);

    // Load a past report when the user picks one from the history selector.
    const viewPastResult = async (responseId) => {
        if (!responseId || responseId === result?.responseId) return;
        setSwitchingId(responseId);
        try {
            const res = await api.get(`/csi/result/${responseId}`);
            if (res.data?.result) setResult(res.data.result);
        } catch {
            /* keep the current report if the past one can't be loaded */
        } finally {
            setSwitchingId(null);
        }
    };

    const pageBg = `min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`;
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';

    if (loading) return <CSIResultSkeleton isDarkMode={isDarkMode} />;

    if (!result || !(result.results || []).length) {
        return (
            <div className={pageBg}>
                <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                    <Gauge className="mx-auto text-primary-500 mb-4" size={40} />
                    <h1 className={`text-2xl font-extrabold mb-2 ${heading}`}>No results yet</h1>
                    <p className={`${sub} mb-6`}>Take the Career Success Index assessment to see your report card.</p>
                    <button onClick={() => navigate('/career-success-index/user?retake=1')} className="px-6 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors">
                        Start assessment
                    </button>
                </div>
            </div>
        );
    }

    const rows = orderResults(result.results);
    const UNLIMITED = 100000;
    const showUsage = usage && usage.limit != null && usage.limit < UNLIMITED;
    const limitReached = showUsage && usage.used >= usage.limit;
    const remaining = showUsage ? Math.max(0, usage.limit - usage.used) : null;
    const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

    return (
        <div className={pageBg}>
            <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
                {/* history selector + usage + actions (outside the printable card) */}
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                        {history.length > 1 && (
                            <div className="relative flex items-center shrink-0">
                                <span className={`mr-2 text-sm ${sub}`}>Viewing</span>
                                <select
                                    value={result?.responseId || ''}
                                    onChange={(e) => viewPastResult(Number(e.target.value))}
                                    className={`appearance-none pr-8 pl-3 py-2 rounded-xl text-sm font-semibold border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-300 text-slate-700'}`}
                                >
                                    {history.map((h, i) => (
                                        <option key={h.responseId} value={h.responseId}>
                                            {fmtDate(h.submittedOn)}{i === 0 ? ' (latest)' : ''}{h.overallPercent != null ? ` · ${Math.round(h.overallPercent)}/100` : ''}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className={`pointer-events-none absolute right-2.5 ${sub}`} />
                            </div>
                        )}
                        {switchingId && <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" aria-label="Loading report" />}
                        {showUsage && (
                            <span className={`text-sm truncate ${limitReached ? 'text-warning font-semibold' : sub}`}>
                                {remaining} of {usage.limit} left this month
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={downloadPdf} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-warning text-white text-sm font-semibold hover:opacity-90 transition-colors">
                            <Download size={16} /> Download PDF
                        </button>
                        {limitReached ? (
                            <button onClick={() => navigate('/membership/plans')} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors">
                                <TrendingUp size={16} /> Upgrade Now
                            </button>
                        ) : (
                            <button onClick={() => navigate('/career-success-index/user?retake=1')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>
                                <RotateCcw size={16} /> Retake
                            </button>
                        )}
                    </div>
                </div>
                {limitReached && (
                    <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? 'border-warning/40 bg-warning/10 text-warning' : 'border-warning/30 bg-warning-tint text-warning'}`}>
                        You've used all {usage.limit} of this month's assessments. Upgrade your plan to retake sooner and unlock more.
                    </div>
                )}

                <CSICoachingReport result={result} isDarkMode={isDarkMode} firstName={firstName} celebrating={celebrate} />
            </div>
            {celebrate && <CSICelebration result={result} onDone={() => setCelebrate(false)} />}
        </div>
    );

    // Self-contained printable report card -> browser "Save as PDF".
    function downloadPdf() {
        const overall = result.overall || {};
        const overallPct = result.overallPercent;
        const submitted = result.submittedOn ? new Date(result.submittedOn).toLocaleDateString() : null;
        const { nextAction } = deriveSummary(rows, overall);
        const locked = !!result.recommendationsLocked;
        const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
        const HEX = { green: '#16a34a', yellow: '#ca8a04', red: '#dc2626', blue: '#94a3b8' };
        const SOFT = { green: '#dcfce7', yellow: '#fef9c3', red: '#fee2e2', blue: '#e2e8f0' };
        const INK = { green: '#15803d', yellow: '#a16207', red: '#b91c1c', blue: '#475569' };
        const C = 2 * Math.PI * 76;
        const strengthRows = rows.filter(r => tone(r.rating) === 'green');
        const focusRows = rows.filter(r => ['yellow', 'red'].includes(tone(r.rating)));

        const bars = rows.map(r => {
            const pct = r.percent != null ? Math.round(r.percent) : 0;
            return `<div class="bar"><span class="bn">${esc(r.focusAreaName)}</span><span class="bt"><span class="bf" style="width:${pct}%;background:${HEX[tone(r.rating)]}"></span></span><span class="bvv">${r.percent != null ? pct : '—'}</span></div>`;
        }).join('');
        const arc = overallPct != null
            ? `<circle cx="100" cy="100" r="76" fill="none" stroke="${HEX[tone(overall.rating)]}" stroke-width="20" stroke-linecap="round" stroke-dasharray="${(overallPct / 100) * C} ${C}" transform="rotate(-90 100 100)"/>`
            : '';
        const item = (r) => {
            const t = tone(r.rating);
            return `<div class="it"><div class="ih"><b>${esc(r.focusAreaName)}</b> <span class="pill" style="background:${SOFT[t]};color:${INK[t]}">${esc(displayRating(r.rating))}</span></div>`
                + (r.description ? `<p class="idesc">${esc(r.description)}</p>` : '')
                + (r.coachingMessage ? `<p class="isup">${esc(r.coachingMessage)}</p>` : '')
                + (r.recommendedAction ? `<p class="isup">▸ ${esc(r.recommendedAction)}</p>` : '')
                + `</div>`;
        };
        const wellCol = strengthRows.length ? `<div class="cb well"><h3>What you’re doing well</h3>${strengthRows.map(item).join('')}</div>` : '';
        const focusCol = focusRows.length ? `<div class="cb focus"><h3>Where to focus next</h3>${focusRows.map(item).join('')}</div>` : '';

        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Career Assessment Report Card</title>
          <style>@page{margin:0}
          *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{font-family:system-ui,Arial,sans-serif;color:#0f172a;max-width:760px;margin:0 auto;padding:26px 24px}
          .banner{background:#1f3a68;color:#fff;font-size:20px;font-weight:800;padding:11px 16px;border-radius:6px}
          .hi{font-size:13px;margin:13px 0 14px;color:#334155}
          .chart{display:flex;align-items:center;gap:20px}
          .donut{position:relative;width:118px;height:118px;flex:none}
          .donut .ct{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800}
          .donut .ct small{font-size:13px;color:#64748b;font-weight:700}
          .bars{flex:1}
          .bar{display:grid;grid-template-columns:88px 1fr 26px;align-items:center;gap:9px;margin-bottom:7px}
          .bar .bn{font-size:12px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
          .bar .bt{position:relative;display:block;height:11px;border-radius:999px;background:#eceae3;overflow:hidden}
          .bar .bf{position:absolute;left:0;top:0;bottom:0;border-radius:999px}
          .bar .bvv{font-size:12px;font-weight:700;text-align:right}
          .hl{font-size:15px;font-weight:700;margin:14px 0 0}
          .next{border:1px solid #bfdbfe;background:#f0f6ff;border-radius:10px;padding:11px 13px;margin:14px 0}
          .next b{display:block;margin-bottom:3px}.next span{font-size:13px;color:#475569}
          .lock{border:1px solid #bfdbfe;background:#f0f6ff;border-radius:10px;padding:16px;margin:14px 0;text-align:center}
          .lock b{display:block;font-size:14px;margin-bottom:4px}.lock span{font-size:12.5px;color:#475569}
          .cols{display:flex;gap:12px;align-items:flex-start}
          .cb{flex:1;border:1px solid #e2e8f0;border-radius:10px;padding:12px}
          .cb.well{background:#f0fdf4;border-color:#bbf7d0}.cb.focus{background:#fef6f6;border-color:#fecdd3}
          .cb h3{font-size:14px;margin:0 0 10px}
          .it{padding:8px 0;border-top:1px solid rgba(0,0,0,.06)}.it:first-of-type{border-top:0;padding-top:0}
          .ih{font-size:13px;margin-bottom:3px}
          .pill{font-size:10px;font-weight:700;padding:1px 6px;border-radius:999px}
          .idesc{font-size:11px;color:#94a3b8;margin:0 0 2px;line-height:1.4}
          .isup{font-size:12px;color:#475569;margin:0;line-height:1.4}
          .muted{color:#64748b;font-size:12px;margin-top:14px}
          .footer{margin-top:18px;padding-top:12px;border-top:1px solid #e2e8f0;text-align:center;color:#64748b;font-size:12px;line-height:1.6}</style></head>
          <body>
            <div class="banner">Career Assessment Report Card</div>
            ${firstName ? `<p class="hi">Hi ${esc(firstName)}, here’s your snapshot. This is a starting point, not a grade.</p>` : ''}
            <div class="chart">
              <div class="donut"><svg width="118" height="118" viewBox="0 0 200 200"><circle cx="100" cy="100" r="76" fill="none" stroke="#eceae3" stroke-width="20"/>${arc}</svg><div class="ct">${overallPct != null ? Math.round(overallPct) : '—'}<small>/100</small></div></div>
              <div class="bars">${bars}</div>
            </div>
            <p class="hl">${esc(overallHeadline(overall.rating))}</p>
            ${locked
                ? `<div class="lock"><b>🔒 Unlock your detailed recommendations</b><span>See what you’re doing well and your personalized next steps for each area — available on any paid plan. Upgrade at buildmybizbrand.com.</span></div>`
                : `${nextAction && nextAction !== '—' ? `<div class="next"><b>Next Action</b><span>${esc(nextAction)}</span></div>` : ''}
            <div class="cols">${wellCol}${focusCol}</div>`}
            ${submitted ? `<div class="muted">Completed ${esc(submitted)}</div>` : ''}
            <div class="footer">Contact us at: coaching@buildmybizbrand.com<br>© ${new Date().getFullYear()} MyBizBrand. All rights reserved.</div>
            <script>window.onload=function(){window.print();}</script>
          </body></html>`;
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
    }
}
